import { useCallback } from "react";
import { z } from "zod";
import { Icon } from "@zapier/zinnia-icons";
import { currentAccountIdAtom } from "@zapier/canvas-state";
import { useAtomValue } from "jotai";

import { useCreateAppConnection } from "./useCreateAppConnection";
import styles from "./buildModeSuggestionsClient.module.css";
import { BuildModeSuggestionsParamsSchema } from "./index";
import { BuildModeSuggestionType } from "./types";
import { ErrorBoundary } from "./ErrorBoundary";
import { promptMapping, iconMapping } from "./copilotSuggestions";
import React from "react";

export type BuildModeSuggestionsParams = z.infer<
  typeof BuildModeSuggestionsParamsSchema
>;

export function BuildModeSuggestions(props: {
  args: BuildModeSuggestionsParams;
}) {
  return (
    <ErrorBoundary>
      <BuildModeSuggestionsInner {...props} />
    </ErrorBoundary>
  );
}

export function BuildModeSuggestionsInner(props: {
  args: BuildModeSuggestionsParams;
}) {
  // Tool components should NOT use useCopilotChat - they're rendered outside the provider context
  // Instead, they should trigger actions that the tool system can respond to
  const accountId = useAtomValue(currentAccountIdAtom);
  const { createNewConnection } = useCreateAppConnection(Number(accountId));
  const args = props?.args || {};
  const { suggestions, selected_api } = args;

  const handleSuggestionClick = useCallback(
    async (suggestion: BuildModeSuggestionType) => {
      const promptText =
        promptMapping[suggestion as keyof typeof promptMapping] || suggestion;

      // Tool components can't directly control the chat - instead, trigger page navigation
      // or use window postMessage to communicate with the parent copilot
      window.postMessage(
        {
          type: "COPILOT_USER_MESSAGE",
          payload: {
            message: promptText,
            source: "build_mode_suggestions",
          },
        },
        "*",
      );
    },
    [],
  );

  const connectApp = useCallback(async () => {
    if (!selected_api) {
      return;
    }
    const res = await fetch(
      `/api/v4/implementations-meta/public?selected_apis=${selected_api}`,
    );
    const data = await res.json();
    const { id } = await createNewConnection(data.results[0]);
    return id;
  }, [selected_api, createNewConnection]);

  const handleButtonClick = useCallback(
    async (suggestion: BuildModeSuggestionType) => {
      // Check if this is an auth creation action
      if (suggestion === BuildModeSuggestionType.CREATE_AUTH) {
        if (!selected_api) {
          // Show error message if CREATE_AUTH is suggested without selected_api
          const errorMessage =
            "Cannot create auth connection: No API selected. The LLM must provide selected_api when suggesting CREATE_AUTH.";

          // Send error message via postMessage
          window.postMessage(
            {
              type: "COPILOT_USER_MESSAGE",
              payload: {
                message: errorMessage,
                source: "build_mode_suggestions_error",
              },
            },
            "*",
          );
          return;
        }

        const id = await connectApp();

        // Push a message to chat about the auth creation
        let authMessage: string = "";
        if (id) {
          authMessage = `Auth created successfully with ID: ${id}`;
        } else {
          authMessage = `Failed to create connection to ${selected_api}`;
        }

        // Send auth result message via postMessage
        window.postMessage(
          {
            type: "COPILOT_USER_MESSAGE",
            payload: {
              message: authMessage,
              source: "build_mode_suggestions_auth",
            },
          },
          "*",
        );
      } else {
        // Otherwise, treat it as a chat suggestion
        await handleSuggestionClick(suggestion);
      }
    },
    [connectApp, handleSuggestionClick, selected_api],
  );

  return (
    <div className={styles.container}>
      {suggestions?.map((suggestion) => {
        const iconName = iconMapping[suggestion as keyof typeof iconMapping];
        return (
          <button
            key={suggestion}
            onClick={() => handleButtonClick(suggestion)}
            className={styles.suggestionButton}
          >
            <Icon
              name={iconName}
              size="20"
              color="UiPrimaryStronger"
              className={styles.buttonIcon}
            />
            <span className={styles.buttonText}>{suggestion}</span>
          </button>
        );
      })}
    </div>
  );
}
